import { Head, Link, router, useForm, usePoll, Deferred } from "@inertiajs/react"
import {
  Video,
  Upload,
  Plus,
  ExternalLink,
  AlertCircle,
  CheckCircle,
  Loader2,
  Calendar,
  CalendarCheck,
  CalendarX,
  Shield,
  RefreshCw,
  Trash2,
  Users,
} from "lucide-react"
import React, { useState, useEffect } from "react"
import { CoachLayout } from "../components/CoachLayout"
import { SectionTabs } from "../components/SectionTabs"

const articlesTabs = [
  { label: "Articles", href: "/app/coach/articles" },
  { label: "Meetings", href: "/app/coach/meetings" },
]
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { UploadTranscriptDialog } from "@/components/UploadTranscriptDialog"
import { Switch } from "@/components/ui/switch"
import { Separator } from "@/components/ui/separator"
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogClose,
} from "@/components/ui/responsive-dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

import { MeetingStatusBadge } from "@/components/meetings/MeetingStatusBadge"

function formatDate(dateString) {
  if (!dateString) return "-"
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

function formatCalendarTime(dateString) {
  if (!dateString) return ""
  const date = new Date(dateString)
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

function isValidMeetingUrl(url) {
  try {
    const parsed = new URL(url)
    return (
      parsed.protocol === "https:" &&
      (parsed.hostname.includes("zoom.us") ||
        parsed.hostname === "meet.google.com")
    )
  } catch {
    return false
  }
}

// --- Calendar Section Components ---

function CalendarNotConnected() {
  const [connecting, setConnecting] = useState(false)

  const handleConnect = () => {
    setConnecting(true)
    router.post("/app/coach/calendar/connect")
  }

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center shrink-0">
            <Calendar className="w-6 h-6 text-zinc-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-zinc-900">
              Connect Google Calendar
            </h3>
            <p className="text-sm text-zinc-600 mt-1">
              Connect your Google Calendar to have the Meeting Bot automatically
              join your meetings. No more pasting meeting URLs.
            </p>
            <p className="text-xs text-zinc-500 mt-2 flex items-center gap-1">
              <Shield className="h-3 w-3" />
              Connecting shares your meeting titles and times with our recording
              service to schedule bots.
            </p>
          </div>
          <Button
            data-tour="google-connect-btn"
            variant="gradient"
            onClick={handleConnect}
            disabled={connecting}
            className="shrink-0"
          >
            {connecting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <Calendar className="h-4 w-4" />
                Connect Calendar
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function CalendarConnected({ calendarAutoJoin, calendarMeetings, calendarLookaheadDays }) {
  const [disconnectOpen, setDisconnectOpen] = useState(false)
  const [autoJoinConfirmOpen, setAutoJoinConfirmOpen] = useState(false)
  const [syncing, setSyncing] = useState(false)
  // The Switch is controlled, so a toggle can only ever mean "the other state".
  const turningOn = !calendarAutoJoin

  const handleSync = () => {
    setSyncing(true)
    router.post("/app/coach/calendar/sync", {}, {
      preserveScroll: true,
      only: ["calendarMeetings"],
      onFinish: () => setSyncing(false),
    })
  }

  // Both directions confirm: OFF because it cancels scheduled bots, ON because
  // the coach needs to hear the rule once — auto-join covers only the meetings
  // they host (RecallAiService::AUTO_JOIN_PREFERENCES).
  const handleAutoJoinToggle = () => setAutoJoinConfirmOpen(true)

  const confirmAutoJoin = () => {
    setAutoJoinConfirmOpen(false)
    router.patch("/app/coach/calendar/update_preferences", {
      auto_join: turningOn ? "true" : "false",
    }, { preserveScroll: true })
  }

  const handleDisconnect = () => {
    setDisconnectOpen(false)
    router.delete("/app/coach/calendar/disconnect")
  }

  return (
    <Card>
      <CardContent className="p-6 space-y-5">
        {/* Connection status + controls */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center shrink-0">
              <CalendarCheck className="w-5 h-5 text-green-600" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-zinc-900">
                Google Calendar Connected
              </h3>
              <p className="text-sm text-zinc-500">
                Upcoming meetings with a video link, synced from your calendar
              </p>
              <p className="text-xs text-zinc-500 mt-1">
                {calendarAutoJoin
                  ? "Auto-join records meetings you organize. Press Record on any other meeting you want recorded."
                  : "Auto-join is off. Press Record on each meeting you want recorded."}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4 shrink-0">
            <div data-tour="auto-join-toggle" className="flex items-center gap-2">
              <Label htmlFor="auto-join" className="text-sm text-zinc-600 whitespace-nowrap">
                Auto-join meetings I host
              </Label>
              <Switch
                id="auto-join"
                checked={calendarAutoJoin}
                onCheckedChange={handleAutoJoinToggle}
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSync}
              disabled={syncing}
              className="text-zinc-500"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Syncing..." : "Sync"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDisconnectOpen(true)}
              className="text-zinc-500"
            >
              Disconnect
            </Button>
          </div>
        </div>

        <Separator />

        {/* Calendar meetings list */}
        <Deferred data="calendarMeetings" fallback={<CalendarSkeleton />}>
          <CalendarMeetingsList calendarMeetings={calendarMeetings} calendarLookaheadDays={calendarLookaheadDays} />
        </Deferred>
      </CardContent>

      {/* Disconnect confirmation */}
      <AlertDialog open={disconnectOpen} onOpenChange={setDisconnectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Google Calendar?</AlertDialogTitle>
            <AlertDialogDescription>
              This will stop the bot from automatically joining your meetings.
              Any scheduled bots will be cancelled. You can reconnect at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDisconnect} className="bg-red-600 hover:bg-red-700">
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Auto-join ON / OFF confirmation */}
      <AlertDialog open={autoJoinConfirmOpen} onOpenChange={setAutoJoinConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {turningOn ? "Auto-join meetings you host?" : "Turn off auto-join?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {turningOn
                ? "The notetaker will join every upcoming meeting that you organize. Meetings someone else organizes — trainings, peer groups, calls on a colleague's Zoom — are not auto-joined. Press Record on any of those you want recorded."
                : "Turning off auto-join will cancel bots scheduled for upcoming meetings. You can still manually select which meetings to record."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{turningOn ? "Cancel" : "Keep Auto-Join"}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmAutoJoin}>
              {turningOn ? "Turn On" : "Turn Off"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}

function CalendarSkeleton() {
  return (
    <div className="space-y-3">
      <div className="h-4 bg-zinc-200 rounded w-48 animate-pulse" />
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-4 p-3 rounded-lg border border-zinc-100 animate-pulse">
          <div className="space-y-2 flex-1">
            <div className="h-4 bg-zinc-200 rounded w-2/3" />
            <div className="h-3 bg-zinc-100 rounded w-1/3" />
          </div>
          <div className="h-8 w-16 bg-zinc-100 rounded" />
        </div>
      ))}
    </div>
  )
}

const CALENDAR_PER_PAGE = 5

function CalendarMeetingsList({ calendarMeetings, calendarLookaheadDays = 30 }) {
  const [calendarPage, setCalendarPage] = useState(1)

  if (!calendarMeetings) return <CalendarSkeleton />

  if (calendarMeetings.error) {
    return (
      <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <AlertCircle className="h-5 w-5 text-amber-600 shrink-0" />
        <div>
          <p className="text-sm text-amber-800">{calendarMeetings.message}</p>
          <button
            onClick={() => router.reload({ only: ["calendarMeetings"] })}
            className="mt-1 text-xs text-amber-700 underline hover:no-underline flex items-center gap-1"
          >
            <RefreshCw className="h-3 w-3" />
            Try again
          </button>
        </div>
      </div>
    )
  }

  // Only show meetings that have a recordable meeting link
  const meetings = (calendarMeetings.data || []).filter((m) => m.hasLink)

  if (meetings.length === 0) {
    return (
      <div className="text-center py-6">
        <CalendarX className="h-8 w-8 text-zinc-300 mx-auto mb-2" />
        <p className="text-sm text-zinc-500">No upcoming meetings with video links in the next {calendarLookaheadDays} days</p>
      </div>
    )
  }

  const totalPages = Math.ceil(meetings.length / CALENDAR_PER_PAGE)
  const startIndex = (calendarPage - 1) * CALENDAR_PER_PAGE
  const paginatedMeetings = meetings.slice(startIndex, startIndex + CALENDAR_PER_PAGE)

  // Reset to page 1 if current page exceeds available pages (e.g. after data refresh)
  if (calendarPage > totalPages && totalPages > 0) {
    setCalendarPage(1)
  }

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium text-zinc-700">
        Upcoming Meetings ({meetings.length})
      </h4>
      {paginatedMeetings.map((meeting) => (
        <CalendarMeetingCard key={meeting.id} meeting={meeting} />
      ))}
      {totalPages > 1 && (
        <PaginationControls
          currentPage={calendarPage}
          totalPages={totalPages}
          onPageChange={setCalendarPage}
        />
      )}
    </div>
  )
}

function CalendarMeetingCard({ meeting }) {
  const [loading, setLoading] = useState(false)
  const [otherCoachConfirmOpen, setOtherCoachConfirmOpen] = useState(false)
  const otherCoaches = meeting.otherCoaches || []

  // Another KF coach is on the invite: one coach records a call, so confirm
  // before this coach books a second calendar onto the same bot.
  const handleRecordClick = () => {
    if (otherCoaches.length > 0) {
      setOtherCoachConfirmOpen(true)
    } else {
      handleRecord()
    }
  }

  const handleRecord = () => {
    setLoading(true)
    router.post(`/app/coach/calendar/meetings/${meeting.id}/record`, {}, {
      preserveScroll: true,
      only: ["calendarMeetings"],
      onFinish: () => setLoading(false),
    })
  }

  const handleSkip = () => {
    setLoading(true)
    router.post(`/app/coach/calendar/meetings/${meeting.id}/skip`, {}, {
      preserveScroll: true,
      only: ["calendarMeetings"],
      onFinish: () => setLoading(false),
    })
  }

  const isRecording = meeting.willRecord || meeting.overrideShouldRecord === true
  const isSkipped = meeting.overrideShouldRecord === false
  const noLink = !meeting.hasLink
  // Shown only on the plain-Record branch below; Recall's own reason
  // ("…not the host…", "…already ended…") is the tooltip.
  const notAutoJoined = meeting.willRecord === false

  return (
    <>
    <div className={`flex items-center gap-4 p-3 rounded-lg border transition-colors ${
      isRecording
        ? "border-green-200 bg-green-50/50"
        : isSkipped
          ? "border-zinc-200 bg-zinc-50"
          : "border-zinc-200"
    }`}>
      {/* Time + title */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-zinc-900 truncate">
          {meeting.title || "Untitled Meeting"}
        </p>
        <div className="flex items-center gap-2 text-xs text-zinc-500 mt-0.5">
          <span>{formatCalendarTime(meeting.startTime)}</span>
          {meeting.platform && (
            <>
              <span className="text-zinc-300">|</span>
              <span className="capitalize">{meeting.platform}</span>
            </>
          )}
        </div>
      </div>

      {/* Status + actions */}
      <div className="shrink-0 flex items-center gap-2">
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
        ) : noLink ? (
          <span className="text-xs text-zinc-400">No meeting link</span>
        ) : isRecording ? (
          <>
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
              <CheckCircle className="h-3 w-3" />
              Scheduled
            </Badge>
            <Button variant="ghost" size="sm" onClick={handleSkip} className="text-xs text-zinc-500 h-7 px-2">
              Skip
            </Button>
          </>
        ) : isSkipped ? (
          <>
            <Badge variant="outline" className="bg-zinc-50 text-zinc-500 border-zinc-200 text-xs">
              Skipped
            </Badge>
            <Button variant="ghost" size="sm" onClick={handleRecordClick} className="text-xs text-zinc-500 h-7 px-2">
              Record
            </Button>
          </>
        ) : (
          <>
            {notAutoJoined && (
              <span
                className="text-xs text-zinc-400 hidden sm:inline"
                title={meeting.willRecordReason || "Auto-join covers only meetings you host."}
              >
                Won't auto-join
              </span>
            )}
            <Button variant="kf-outline" size="sm" onClick={handleRecordClick} className="text-xs h-7">
              Record
            </Button>
          </>
        )}
      </div>
    </div>

    <AlertDialog open={otherCoachConfirmOpen} onOpenChange={setOtherCoachConfirmOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you the one recording this call?</AlertDialogTitle>
          <AlertDialogDescription>
            {otherCoaches.join(", ")} {otherCoaches.length === 1 ? "is" : "are"} also on this
            call. One coach records a call, and that coach gets the article. If you both
            record, you will both be asked who owns it. Interviewing each other? Use two
            separate Zoom meetings and each record your own.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleRecord}>
            Yes, I'm recording
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  )
}

// Recordings two or more coaches booked. Nothing is generated until one of
// them claims it here; the first claim wins (Meeting#claim!).
function ClaimableMeetings({ claimableMeetings }) {
  const [claiming, setClaiming] = useState(null)

  if (!claimableMeetings?.length) return null

  const handleClaim = (id) => {
    setClaiming(id)
    router.post(`/app/coach/meetings/${id}/claim`, {}, {
      preserveScroll: true,
      onFinish: () => setClaiming(null),
    })
  }

  return (
    <Card data-tour="claimable-meetings" className="border-amber-200 bg-amber-50/40">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <Users className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-zinc-900">Who owns this recording?</h3>
            <p className="text-xs text-zinc-600 mt-0.5">
              Two coaches recorded the same call. Claim it if the article is yours — the first
              coach to claim gets the draft. Next time, only one coach records a call.
            </p>
          </div>
        </div>
        {claimableMeetings.map((meeting) => (
          <div key={meeting.id} className="flex items-center gap-4 p-3 rounded-lg border border-amber-200 bg-white">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-zinc-900 truncate">
                {meeting.title || "Calendar Meeting"}
              </p>
              <p className="text-xs text-zinc-500 mt-0.5">
                {formatDate(meeting.scheduledStartAt || meeting.createdAt)}
                {meeting.otherCoaches?.length > 0 && (
                  <> &middot; also recorded by {meeting.otherCoaches.join(", ")}</>
                )}
              </p>
            </div>
            <Button
              variant="gradient"
              size="sm"
              onClick={() => handleClaim(meeting.id)}
              disabled={claiming === meeting.id}
              className="shrink-0"
            >
              {claiming === meeting.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Claim"}
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

// --- Pagination Components ---

function generatePageNumbers(currentPage, totalPages) {
  const pages = []
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i)
  } else {
    pages.push(1)
    if (currentPage > 3) pages.push("ellipsis-start")
    const start = Math.max(2, currentPage - 1)
    const end = Math.min(totalPages - 1, currentPage + 1)
    for (let i = start; i <= end; i++) pages.push(i)
    if (currentPage < totalPages - 2) pages.push("ellipsis-end")
    pages.push(totalPages)
  }
  return pages
}

function PaginationControls({ currentPage, totalPages, onPageChange }) {
  const pages = generatePageNumbers(currentPage, totalPages)

  return (
    <Pagination className="pt-3">
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            className={currentPage <= 1 ? "pointer-events-none opacity-50" : ""}
          />
        </PaginationItem>
        {pages.map((page) =>
          typeof page === "string" ? (
            <PaginationItem key={page}>
              <PaginationEllipsis />
            </PaginationItem>
          ) : (
            <PaginationItem key={page}>
              <PaginationLink
                isActive={page === currentPage}
                onClick={() => onPageChange(page)}
              >
                {page}
              </PaginationLink>
            </PaginationItem>
          )
        )}
        <PaginationItem>
          <PaginationNext
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            className={currentPage >= totalPages ? "pointer-events-none opacity-50" : ""}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  )
}

function ServerPaginationControls({ currentPage, totalPages }) {
  const pages = generatePageNumbers(currentPage, totalPages)

  return (
    <Pagination className="py-4">
      <PaginationContent>
        <PaginationItem>
          <Link
            href={`/app/coach/meetings?page=${Math.max(1, currentPage - 1)}`}
            preserveScroll
            className={currentPage <= 1 ? "pointer-events-none opacity-50" : ""}
          >
            <PaginationPrevious as="span" />
          </Link>
        </PaginationItem>
        {pages.map((page) =>
          typeof page === "string" ? (
            <PaginationItem key={page}>
              <PaginationEllipsis />
            </PaginationItem>
          ) : (
            <PaginationItem key={page}>
              <Link
                href={`/app/coach/meetings?page=${page}`}
                preserveScroll
              >
                <PaginationLink as="span" isActive={page === currentPage}>
                  {page}
                </PaginationLink>
              </Link>
            </PaginationItem>
          )
        )}
        <PaginationItem>
          <Link
            href={`/app/coach/meetings?page=${Math.min(totalPages, currentPage + 1)}`}
            preserveScroll
            className={currentPage >= totalPages ? "pointer-events-none opacity-50" : ""}
          >
            <PaginationNext as="span" />
          </Link>
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  )
}

// --- Dialog Components ---

function StartRecordingDialog({ open, onOpenChange }) {
  const [clientErrors, setClientErrors] = useState({})

  const form = useForm({
    meeting_url: "",
    source: "recall_bot",
  })

  const validate = () => {
    const errors = {}
    if (!form.data.meeting_url.trim()) {
      errors.meeting_url = "Meeting URL is required"
    } else if (!isValidMeetingUrl(form.data.meeting_url)) {
      errors.meeting_url = "Must be a valid Zoom or Google Meet link"
    }
    setClientErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!validate()) return

    form.post("/app/coach/meetings", {
      preserveScroll: true,
      onSuccess: () => {
        onOpenChange(false)
      },
    })
  }

  const handleClose = (isOpen) => {
    if (!isOpen) {
      setClientErrors({})
      form.reset()
      form.clearErrors()
    }
    onOpenChange(isOpen)
  }

  const errorFor = (field) => clientErrors[field] || form.errors?.[`meeting.${field}`]

  return (
    <ResponsiveDialog open={open} onOpenChange={handleClose}>
      <ResponsiveDialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="font-display text-xl">
              Start Meeting Recording
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              Paste your Zoom or Google Meet link and the KF bot will join to record.
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div className="space-y-4 py-4 px-4 sm:px-0">
            <div className="space-y-2">
              <Label htmlFor="meeting_url">Meeting URL</Label>
              <Input
                id="meeting_url"
                value={form.data.meeting_url}
                onChange={(e) => form.setData("meeting_url", e.target.value)}
                placeholder="https://zoom.us/j/... or https://meet.google.com/..."
              />
              {errorFor("meeting_url") && (
                <p className="text-sm text-red-600">{errorFor("meeting_url")}</p>
              )}
            </div>

            <p className="text-xs text-zinc-500">
              The bot will join your meeting automatically. You can close this page safely.
            </p>
          </div>

          <ResponsiveDialogFooter>
            <ResponsiveDialogClose asChild>
              <Button type="button" variant="outline">Cancel</Button>
            </ResponsiveDialogClose>
            <Button type="submit" variant="gradient" disabled={form.processing}>
              {form.processing ? "Starting..." : "Start Recording"}
            </Button>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

// --- Main Page ---

export default function MeetingsIndex({
  currentUser,
  meetings,
  pagination,
  stats,
  claimableMeetings,
  calendarConnected,
  calendarAutoJoin,
  calendarMeetings,
  calendarLookaheadDays = 30,
}) {
  const [recordingOpen, setRecordingOpen] = useState(false)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)

  const handleDelete = () => {
    if (!deleteTarget) return
    router.delete(`/app/coach/meetings/${deleteTarget}`, {
      onFinish: () => setDeleteTarget(null),
    })
  }

  // Fast polling when active meetings exist (calendar data excluded — use Sync button instead)
  const { start: startFast, stop: stopFast } = usePoll(
    10000,
    { only: ["meetings", "stats", "pagination", "claimableMeetings"] },
    { autoStart: false }
  )

  // Slow polling when all meetings are idle
  const { start: startSlow, stop: stopSlow } = usePoll(
    30000,
    { only: ["meetings", "stats", "pagination", "claimableMeetings"] },
    { autoStart: false }
  )

  useEffect(() => {
    if (stats?.active > 0) {
      stopSlow()
      startFast()
    } else {
      stopFast()
      startSlow()
    }
    return () => {
      stopFast()
      stopSlow()
    }
  }, [stats?.active])

  const statCards = [
    { label: "Total", value: stats?.total || 0 },
    { label: "Active", value: stats?.active || 0 },
    { label: "Completed", value: stats?.completed || 0 },
    { label: "Failed", value: stats?.failed || 0 },
  ]

  return (
    <CoachLayout currentUser={currentUser}>
      <Head title="Meetings - Kingdom Factor" />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Meetings</h1>
            <p className="text-zinc-600">Record interviews and generate article drafts</p>
          </div>
          <div className="flex gap-2">
            <Button data-tour="upload-transcript" variant="kf-outline" onClick={() => setUploadOpen(true)}>
              <Upload className="h-4 w-4" />
              Upload Transcript
            </Button>
            <Button data-tour="start-recording" variant="gradient" onClick={() => setRecordingOpen(true)}>
              <Video className="h-4 w-4" />
              Start Recording
            </Button>
          </div>
        </div>

        <SectionTabs tabs={articlesTabs} />

        {/* Calendar Integration */}
        <div data-tour="calendar-section">
          {calendarConnected ? (
            <CalendarConnected
              calendarAutoJoin={calendarAutoJoin}
              calendarMeetings={calendarMeetings}
              calendarLookaheadDays={calendarLookaheadDays}
            />
          ) : (
            <CalendarNotConnected />
          )}
        </div>

        <ClaimableMeetings claimableMeetings={claimableMeetings} />

        {/* Stats */}
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          {statCards.map((stat) => (
            <div
              key={stat.label}
              className="p-4 rounded-lg border border-zinc-200 bg-white"
            >
              <p className="text-sm text-zinc-600">{stat.label}</p>
              <p className="text-2xl font-bold">{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Meetings Table */}
        <Card data-tour="meetings-table">
          <CardContent className="p-0">
            {meetings?.length > 0 ? (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Interviewee</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Article</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {meetings.map((meeting, index) => (
                      <TableRow
                        key={meeting.id}
                        data-tour={index === 0 ? "meeting-row" : undefined}
                        className="cursor-pointer"
                        onClick={() => router.visit(`/app/coach/meetings/${meeting.id}`)}
                      >
                        <TableCell className="text-zinc-600">
                          {formatDate(meeting.scheduledStartAt || meeting.createdAt)}
                        </TableCell>
                        <TableCell className="font-medium">
                          {meeting.source === "calendar"
                            ? (meeting.title || "Calendar Meeting")
                            : (meeting.intervieweeName || "-")}
                          {meeting.intervieweeCompany && (
                            <span className="text-zinc-500 text-sm block">
                              {meeting.intervieweeCompany}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">
                            {meeting.source === "recall_bot"
                              ? "Bot"
                              : meeting.source === "calendar"
                                ? "Calendar"
                                : "Upload"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <MeetingStatusBadge status={meeting.status} />
                        </TableCell>
                        <TableCell>
                          {meeting.articleId ? (
                            <Link
                              href={`/app/coach/articles/${meeting.articleId}`}
                              className="text-kf-red hover:underline text-sm flex items-center gap-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="h-3 w-3" />
                              {meeting.articleTitle || "View Article"}
                            </Link>
                          ) : meeting.errorMessage ? (
                            <span className="text-sm text-red-600 truncate max-w-[200px] block">
                              {meeting.errorMessage}
                            </span>
                          ) : (
                            <span className="text-zinc-400 text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {/* A parked recording leaves only through Claim; the server refuses destroy too. */}
                          {meeting.status !== "awaiting_claim" && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                setDeleteTarget(meeting.id)
                              }}
                              className="p-1.5 rounded-md text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {pagination?.totalPages > 1 && (
                  <ServerPaginationControls
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                  />
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 px-4">
                <div className="w-14 h-14 rounded-full bg-zinc-100 flex items-center justify-center mb-4">
                  <Video className="w-7 h-7 text-zinc-400" />
                </div>
                <h3 className="text-lg font-semibold text-zinc-900 mb-1">
                  No meetings yet
                </h3>
                <p className="text-zinc-600 text-center mb-6 max-w-sm">
                  Submit a meeting recording URL or upload a transcript to get started
                  with article generation.
                </p>
                <Button variant="gradient" onClick={() => setRecordingOpen(true)}>
                  <Plus className="h-4 w-4" />
                  Start Your First Recording
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <StartRecordingDialog open={recordingOpen} onOpenChange={setRecordingOpen} />
      <UploadTranscriptDialog open={uploadOpen} onOpenChange={setUploadOpen} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this meeting?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the meeting recording and any associated data.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </CoachLayout>
  )
}
